import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// Removed FontAwesome icons - using simple text/HTML elements instead

const DateContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 1rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const CalendarSection = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  button {
    background: none;
    border: none;
    color: ${props => props.theme.colors.accent};
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: all 0.2s ease;
    
    &:hover {
      background: ${props => props.theme.colors.accent};
      color: white;
    }
  }
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin: 0;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const WeekDay = styled.div`
  text-align: center;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem;
  font-size: 0.9rem;
`;

const Day = styled.button`
  aspect-ratio: 1;
  border: none;
  background: ${({ availability, isSelected, theme }) => {
    if (isSelected) return theme.colors.accent;
    switch (Number(availability)) {
      case 1: return 'white';    // Available
      case 2: return '#ffd700';  // Limited Availability
      case 3: return '#90ee90';  // Booking Duration
      case 4: return '#e0e0e0';  // Not Available
      default: return theme.colors.greyBackground;
    }
  }};
  color: ${({ isSelected, availability }) => 
    isSelected 
      ? 'white' 
      : Number(availability) === 4
        ? '#999' 
        : '#000'};
  border-radius: 8px;
  cursor: ${({ availability }) => Number(availability) === 1 ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: 500;
  
  &:hover {
    transform: ${({ availability }) => Number(availability) === 1 ? 'scale(1.05)' : 'none'};
    background: ${({ availability, isSelected, theme }) => 
      !isSelected && Number(availability) === 1 ? theme.colors.offWhite : ''};
  }
`;

const CalendarLegend = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.secondary};
  
  span {
    width: 1rem;
    height: 1rem;
    border-radius: 4px;
    background: ${({ color }) => color};
  }
`;

const TimeSlotsSection = styled.div`
  position: sticky;
  top: 90px;
  height: fit-content;
  background: white;
  border-radius: 10px;
  padding: 1rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
`;

const TimeSlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const TimeSlot = styled.button`
  padding: 1rem;
  border: 2px solid ${props => props.$isSoldOut
    ? 'rgba(16, 29, 64, 0.08)'
    : (props.isSelected ? props.theme.colors.accent : props.theme.colors.greyBackground)};
  background: ${props => props.$isSoldOut
    ? 'linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%)'
    : (props.isSelected ? props.theme.colors.accent : 'white')};
  color: ${props => props.$isSoldOut ? '#6b7280' : (props.isSelected ? 'white' : props.theme.colors.primary)};
  border-radius: 8px;
  cursor: ${props => (props.$isSoldOut ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: 500;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.accent};
    background: ${props => props.isSelected ? props.theme.colors.accent : props.theme.colors.offWhite};
  }
  
  &:disabled {
    opacity: ${props => (props.$isSoldOut ? 1 : 0.5)};
    cursor: not-allowed;
  }
`;

const SoldOutLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-top: 5px;
  text-align: center;
`;

const SelectedDateInfo = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  text-align: center;
`;

const SelectedDateText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const SelectedTimeText = styled.div`
  font-size: 1rem;
  opacity: 0.9;
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd, #f3e5f5);
  border: 1px solid ${props => props.theme.colors.accent};
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const DateSelection = ({ 
  availableDates, 
  selectedDate, 
  timeSlots, 
  selectedTimeSlot, 
  onDateSelect, 
  onTimeSlotSelect 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Function to find the next available date
  const findNextAvailableDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Convert availableDates object to array of dates and sort them
    const availableDateStrings = Object.keys(availableDates || {})
      .filter(dateString => {
        const date = new Date(dateString);
        const availability = availableDates[dateString];
        // Only include future dates that are available (availability === 1)
        return date >= today && availability === 1;
      })
      .sort(); // Sort chronologically
    
    if (availableDateStrings.length > 0) {
      return new Date(availableDateStrings[0]);
    }
    
    // If no available dates found, return current month
    return new Date();
  };

  // Update currentMonth when availableDates changes to show the month with next available date
  useEffect(() => {
    if (availableDates && Object.keys(availableDates).length > 0) {
      const nextAvailableDate = findNextAvailableDate();

      setCurrentMonth(nextAvailableDate);
    }
  }, [availableDates]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay };
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  const getDateAvailability = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return availableDates[dateString] || 4; // Default to not available
  };

  const renderCalendar = () => {
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const { days, firstDay } = getDaysInMonth(currentMonth);
    const today = new Date();

    return (
      <CalendarSection>
        <CalendarHeader>
          <button onClick={handlePrevMonth}>&lt;</button>
          <h3>
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h3>
          <button onClick={handleNextMonth}>&gt;</button>
        </CalendarHeader>
        <CalendarGrid>
          {weekDays.map(day => (
            <WeekDay key={day}>{day}</WeekDay>
          ))}
          {[...Array(firstDay)].map((_, index) => (
            <Day key={`empty-${index}`} availability={0} />
          ))}
          {[...Array(days)].map((_, index) => {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), index + 1);
            const availability = getDateAvailability(date);
            const isSelected = selectedDate &&
              date.getDate() === selectedDate.getDate() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getFullYear() === selectedDate.getFullYear();
            const isPast = date < today;

            return (
              <Day
                key={index}
                availability={isPast ? 4 : availability}
                isSelected={isSelected}
                onClick={() => !isPast && availability === 1 && onDateSelect(date)}
                disabled={isPast || availability !== 1}
              >
                {index + 1}
              </Day>
            );
          })}
        </CalendarGrid>
        <CalendarLegend>
          <LegendItem color="#e0e0e0">
            <span></span>Not Available
          </LegendItem>
          <LegendItem color="#ffd700">
            <span></span>Limited Availability
          </LegendItem>
          <LegendItem color="#90ee90">
            <span></span>Booking Duration
          </LegendItem>
          <LegendItem color="white">
            <span></span>Available
          </LegendItem>
        </CalendarLegend>
      </CalendarSection>
    );
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    return timeSlot.time || timeSlot;
  };

  return (
    <DateContainer>
      <SectionTitle>
        📅 Select Your Visit Date
      </SectionTitle>

      <Layout>
        <div>
          {renderCalendar()}

          {selectedDate && (
            <SelectedDateInfo>
              <SelectedDateText>
                ✓ Selected: {formatDate(selectedDate)}
              </SelectedDateText>
              {selectedTimeSlot && (
                <SelectedTimeText>
                  🕐 Time: {formatTime(selectedTimeSlot)}
                </SelectedTimeText>
              )}
            </SelectedDateInfo>
          )}

          <InfoBox>
            <InfoText>
              ℹ️ Select your preferred visit date from the calendar above. Available dates are shown in white.
              {timeSlots.length > 0 && ' Choose a time slot if available.'}
            </InfoText>
          </InfoBox>
        </div>

        {selectedDate && (
          <TimeSlotsSection>
            <h3>
              🕐 Available Time Slots
            </h3>
            {timeSlots.length > 0 ? (
              <TimeSlotsGrid>
                {timeSlots.map((timeSlot, index) => {
                  const quantityAvailable = typeof timeSlot === 'object'
                    ? timeSlot.quantityAvailable
                    : undefined;
                  const isSoldOut = quantityAvailable === 0;
                  return (
                    <div key={index}>
                      <TimeSlot
                        type="button"
                        isSelected={!isSoldOut && selectedTimeSlot === timeSlot}
                        $isSoldOut={isSoldOut}
                        disabled={isSoldOut}
                        title={isSoldOut ? 'This time slot is fully booked' : undefined}
                        onClick={() => {
                          if (isSoldOut) return;
                          onTimeSlotSelect(timeSlot);
                        }}
                      >
                        {formatTime(timeSlot)}
                      </TimeSlot>
                      {isSoldOut && <SoldOutLabel>Sold</SoldOutLabel>}
                    </div>
                  );
                })}
              </TimeSlotsGrid>
            ) : (
              <div>No specific timeslots required for this date.</div>
            )}
          </TimeSlotsSection>
        )}
      </Layout>
    </DateContainer>
  );
};

export default DateSelection;